import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  isRedirect,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { SignInForm } from "~/components/auth/sign-in-form";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { CDNImage } from "~/components/ui/cdn-image";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { useTracking } from "~/contexts/tracking";
import ImageDcmLogoHorizontalWhiteUrl from "~/img/logo/dcm-logo-horizontal-white.png";
import { getClerkErrorMessage } from "~/lib/clerk-errors";
import {
  checkUserSummitRegistration,
  getNextAvailableSummitOptions,
  registerAuthenticatedUserForSummit,
  registerUnauthenticatedUserForSummit,
} from "~/lib/functions/summit-registration";
import { createBrandMeta } from "~/lib/shared/meta";
import { formatSummitDateRange } from "~/lib/summit";

const RegistrationFormSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  email: z.string().email("Valid email is required"),
});

type RegistrationForm = z.infer<typeof RegistrationFormSchema>;

export const Route = createFileRoute("/summit/register")({
  beforeLoad: async () => {
    // Check if user is already authenticated and registered
    try {
      const { fetchClerkAuth } = await import("~/routes/__root");
      const { userId } = await fetchClerkAuth();

      if (userId) {
        const registration = await checkUserSummitRegistration();
        if (registration.isRegistered) {
          // Already registered → go to agenda
          throw redirect({ to: "/summit/agenda" });
        }
        // Authenticated but not registered → show page to register
      }
      // Not authenticated → show page to sign up
    } catch (error) {
      // Rethrow redirects — the `/summit/agenda` redirect above, and the
      // handshake redirect that `fetchClerkAuth` throws when the session cookie
      // is stale. Anything else: fall through and show the page.
      if (isRedirect(error)) {
        throw error;
      }
    }
  },
  head: () =>
    createBrandMeta({
      title: "Register for Summit",
      description:
        "Register to access the full summit agenda and connect with expert coaches.",
      ogType: "website",
    }),
  component: SummitRegisterPage,
});

function SummitRegisterPage(): React.ReactElement {
  const [isProcessingSignin, setIsProcessingSignin] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(
    null,
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { parameters, searchParams } = useTracking();

  const { data: summitData, isLoading } = useQuery(
    getNextAvailableSummitOptions,
  );
  const summit = summitData?.summit ?? null;

  const form = useForm<RegistrationForm>({
    resolver: zodResolver(RegistrationFormSchema),
    defaultValues: { firstName: "", email: "" },
  });

  const enrichRedirectUrl = useCallback(
    (url: string): string => {
      if (typeof window === "undefined") return url;
      try {
        const parsed = new URL(url, window.location.origin);
        searchParams.forEach((value, key) => {
          if (!parsed.searchParams.has(key)) {
            parsed.searchParams.set(key, value);
          }
        });
        return parsed.toString();
      } catch {
        return url;
      }
    },
    [searchParams],
  );

  const registerMutation = useMutation({
    mutationFn: registerAuthenticatedUserForSummit,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["summit", "user-registration"],
      });
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        navigate({ to: "/summit/agenda" });
      }
    },
  });

  const unauthenticatedRegisterMutation = useMutation({
    mutationFn: registerUnauthenticatedUserForSummit,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["summit", "user-registration"],
      });
      if (data.redirectUrl) {
        window.location.href = enrichRedirectUrl(data.redirectUrl);
      } else {
        navigate({ to: "/summit/agenda" });
      }
    },
    onError: (error) => {
      setRegistrationError(
        getClerkErrorMessage(error, "Failed to register for summit"),
      );
    },
  });

  const handleSigninSuccess = useCallback(async () => {
    setIsProcessingSignin(true);
    try {
      const registration = await checkUserSummitRegistration();
      if (!registration.isRegistered && summit) {
        await registerMutation.mutateAsync({
          data: {
            summitId: summit.id,
            redirectUrl: "/summit/agenda",
          },
        });
      } else {
        navigate({ to: "/summit/agenda" });
      }
    } catch {
      navigate({ to: "/summit/agenda" });
    }
  }, [summit, registerMutation, navigate]);

  const handleRegistrationSubmit = form.handleSubmit((data) => {
    if (!summit) return;
    setRegistrationError(null);
    unauthenticatedRegisterMutation.mutate({
      data: {
        firstName: data.firstName,
        email: data.email,
        summitId: summit.id,
        redirectUrl: "/summit/agenda",
        trackingParameters:
          Object.keys(parameters).length > 0 ? parameters : undefined,
      },
    });
  });

  const summitDateRange =
    summit?.startDate && summit?.endDate
      ? formatSummitDateRange(summit.startDate, summit.endDate)
      : null;

  const isRegistering = unauthenticatedRegisterMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-purple-900">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="relative min-h-dvh">
      {/* Full-page background */}
      <CDNImage
        src="https://form-data.dreamcoachmatch.com/summit/summit-bg.jpg"
        alt=""
        width={2400}
        className="fixed inset-0 h-full w-full object-cover"
        loading="eager"
        priority
        aria-hidden="true"
      />
      <div className="fixed inset-0 bg-purple-900/70" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10 flex min-h-dvh flex-col items-center justify-center px-4 py-8 md:px-6">
        {/* Logo and Summit Info */}
        <div className="mb-8 text-center">
          <img
            src={ImageDcmLogoHorizontalWhiteUrl}
            alt="Dream Coach Match"
            className="mx-auto mb-6 h-6 w-auto opacity-90 md:h-8"
          />
          {summit && (
            <>
              <h1 className="mb-2 font-heading text-3xl tracking-tight text-white md:text-5xl">
                {summit.title}
              </h1>
              {summitDateRange && (
                <p className="text-sm font-medium text-purple-200 md:text-base">
                  {summitDateRange}
                </p>
              )}
              <h2 className="mt-8 text-lg text-purple-200/80 italic">
                Register to access the full agenda
              </h2>
            </>
          )}
        </div>

        {/* Auth Card */}
        <Card className="w-full max-w-md p-6 shadow-2xl">
          {isProcessingSignin ? (
            <div className="py-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              <p className="text-text-secondary mt-4">
                Completing your registration...
              </p>
            </div>
          ) : (
            <>
              {/* Login section */}
              <div>
                <h3 className="font-heading text-lg font-semibold">
                  Already registered?
                </h3>
                <p className="text-text-secondary mb-4 text-sm">
                  Log in with a one-time code
                </p>
                <SignInForm
                  singleStrategy="email_code"
                  onSuccess={handleSigninSuccess}
                />
              </div>

              {/* Divider */}
              <div className="my-6 flex items-center gap-4">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-text-secondary text-sm">or</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* Registration section */}
              <div>
                <h3 className="font-heading text-lg font-semibold">
                  Not registered yet?
                </h3>
                <p className="text-text-secondary mb-4 text-sm">
                  Register in seconds
                </p>
                <form onSubmit={handleRegistrationSubmit} className="space-y-4">
                  <FormField
                    label="First Name"
                    error={form.formState.errors.firstName?.message}
                  >
                    <Input
                      {...form.register("firstName")}
                      placeholder="First name"
                      error={form.formState.errors.firstName?.message}
                      disabled={isRegistering}
                    />
                  </FormField>
                  <FormField
                    label="Email Address"
                    error={form.formState.errors.email?.message}
                  >
                    <Input
                      {...form.register("email")}
                      type="email"
                      placeholder="your@email.com"
                      error={form.formState.errors.email?.message}
                      disabled={isRegistering}
                    />
                  </FormField>
                  <Button
                    type="submit"
                    loading={isRegistering}
                    disabled={isRegistering}
                    className="w-full font-extrabold uppercase"
                    size="xl"
                    variant="primary"
                  >
                    {isRegistering ? "Registering..." : "Register Now"}
                  </Button>
                </form>
                {registrationError && (
                  <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-700">{registrationError}</p>
                  </div>
                )}
              </div>

              {/* Footer link */}
              <div className="mt-6 border-t border-gray-200 pt-4 text-center">
                <Link
                  to="/summit"
                  className="text-text-secondary text-sm hover:text-brand-600 hover:underline"
                >
                  Learn more about the summit
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
